<template>
  <fts-design-modal
    desc="defining a new state or modifying an existing state in this module"
    class="fts-state-design-modal"
    ref="ftsDesignModal"
  >
    <!-- title -->
    <template #title>
      <!-- studio title -->
      <div class="design-modal-header-std__title with-hint state-design-modal-header">
        <div class="state-design-input-wrapper">
          <input
            type="text"
            placeholder="Please enter a name"
            class="fts-form-control design-modal-header-input"
            :class="{error: $v.formData.name && $v.formData.name.$error}"
            :style="[getStyle, getInputWidth]"
            v-model="formData.name"
            spellcheck="false"
            @focus="designNameHasFocus = true"
            @blur="designNameHasFocus = false"
            @input="dispatchAutofill"
            @keydown.enter="doneContinue"
            ref="designerName"
          />
        </div>
        <div class="design-modal-header-hint" v-if="headerHintHelper">press enter to continue</div>
        <!-- color -->
        <swatches
          class="design-modal-header-swatches"
          v-model="formData.color"
          row-length="7"
          :swatches="colors"
          shapes="circles"
          popover-x="right"
          :swatch-size="25"
          background-color="var(--fts-popover-alt-background)"
        />
      </div>
      <!-- error message -->
      <div class="design-modal-form-error" v-if="error('name')">{{error('name')}}</div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">State</span>
      <span class="design-modal-header-description">
        defining a new state or modifying an existing state in this module
      </span>
    </template>
    <!-- body -->
    <template #body>
      <!-- ai annotation -->
      <fts-ai-annotation
        class="design-modal-ai-annotation"
        :instruction="formData.ai_instruction"
        :hint="formData.ai_hint"
      />
      <!-- description -->
      <div class="fts-form-group">
        <fts-contenteditable
          v-model="formData.description"
          text-wrap
          placeholder="enter description (optional)"
        />
      </div>
      <!-- flow design --> 
      <!-- <div class="dm-collapsible design-modal-form-group" v-if="isExist">

        <div class="dm-collapsible-header">
          <div class="dm-collapsible-header-wrapper">

            <div class="dm-collapsible-header__title">
              <div class="dm-collapsible-header-title">
                Flow
              </div>

              <fts-tippy content="Open flow diagram" v-if="!builder">
                <div slot="trigger" class="dm-collapsible-header-tag-action" @click="openWorkflow">
                  <font-awesome-icon :icon="['fas', 'project-diagram']" class="dm-collapsible-header-tag-icon" />
                  <div class="dm-collapsible-header-tag-label">
                    Show Diagram
                  </div>
                </div>
              </fts-tippy>
            </div>

            <div class="dm-collapsible-header-description">
              Following are activities that related to this state
            </div>
          </div>
        </div>

        <div class="dm-collapsible-content">

          <div class="dm-flow-section" v-if="formData.initial">
            <div class="fts-flow-display dm-flow-display">
              <div class="dm-flow-wrapper">

                <div class="dm-flow-state-start">
                  start
                </div>

                <font-awesome-icon :icon="['fas', 'right-long']" class="dm-flow-display-direction" />

                <fts-activity-design-inline
                  :activity="{
                    id: initStandardId,
                    name: 'create' 
                  }"
                  :list="standardActivities"
                  @navigate="navigateStandardDesign"
                />

                <font-awesome-icon :icon="['fas', 'right-long']" class="dm-flow-display-direction" />

                <fts-state-design-inline 
                  :state="formData"
                  :editable="false"
                  :navigatable="false"
                  @navigate:activity="navigateNewActivityDesign"
                />
              </div>
            </div>
          </div>

          <div class="dm-flow-section" v-for="x in stateFlows" :key="x.id">
            <template v-if="x.states && x.states.length">
              <div class="dm-flow-section-title">{{x.name}}</div>
              <div
                v-for="y in x.states"
                :key="y.id"
                class="fts-flow-display dm-flow-display"
              >
                <div class="dm-flow-wrapper">

                  <fts-state-design-inline 
                    :state="formData"
                    :editable="false"
                    :navigatable="false"
                    @navigate:activity="navigateNewActivityDesign"
                    v-if="y.fromStateId == formData.id"
                  />
                  <fts-state-design-inline 
                    :state-id="y.fromStateId"
                    :list="states"
                    :editable="false"
                    @navigate="navigateStateDesign"
                    @navigate:activity="navigateNewActivityDesign"
                    v-else
                  />

                  <font-awesome-icon :icon="['fas', 'right-long']" class="dm-flow-display-direction" />

                  <fts-activity-design-inline
                    :activity-id="y.activityId"
                    :list="activities"
                    @navigate="navigateActivityDesign"
                    v-if="y.activityId"
                  />
                  <fts-activity-design-inline
                    :activity="{ name: '(no activity)' }"
                    :navigatable="false"
                    class="fts-text-default"
                    v-else
                  />

                  <font-awesome-icon :icon="['fas', 'right-long']" class="dm-flow-display-direction" />

                  <template v-if="y.conditional">
                    <template v-for="(state, stateIndex) in y.conditionalStates">
                      <div :key="`or-${state.id}`" v-if="stateIndex" class="dm-flow-display-direction">
                        or
                      </div>
                      <fts-state-design-inline
                        :key="`state-${state.id}`"
                        :state="formData"
                        :editable="false"
                        :navigatable="false"
                        @navigate:activity="navigateNewActivityDesign"
                        v-if="state.toStateId == formData.id"
                      />
                      <fts-state-design-inline
                        :key="`state-${state.id}`"
                        :state-id="state.toStateId"
                        :list="states"
                        :editable="false"
                        @navigate="navigateStateDesign"
                        @navigate:activity="navigateNewActivityDesign"
                        v-else
                      />
                    </template>
                  </template>
                  <template v-else>
                    <fts-state-design-inline
                      :state="formData"
                      :editable="false"
                      :navigatable="false"
                      @navigate:activity="navigateNewActivityDesign"
                      v-if="y.toStateId == formData.id"
                    />
                    <fts-state-design-inline
                      :state-id="y.toStateId"
                      :list="states"
                      :editable="false"
                      @navigate="navigateStateDesign"
                      @navigate:activity="navigateNewActivityDesign"
                      v-else
                    />
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="dm-collapsible-footer">
          <fts-btn outline color="primary" text-transform-init @click="navigateNewActivityDesign(formData.id)">
            <font-awesome-icon :icon="['fas', 'plus']" />Add new Activity
          </fts-btn>
        </div>
      </div> -->
      <!-- advanced -->
      <fts-collapsible-element
        v-model="advanced"
        v-if="isAdvancedSettings"
      >
        <fts-btn
          slot="toggle"
          slot-scope="{ isVisible, toggle }"
          text
          text-transform-init
          :class="{ open: isVisible }"
          class="design-modal-advanced-toggle"
          @click="toggle()"
        >
          Advanced settings
          <font-awesome-icon
            :icon="['fas', 'caret-right']"
            class="design-modal-advanced-toggle-icon"
          />
        </fts-btn>
        <div slot="content" class="design-modal-advanced-content fts-row gutters">
          <!-- initial -->
          <fts-form-element
            :item="{
              fieldName: 'initial',
              input: 'toggleSwitch',
            }"
            :form-data="formData"
            comfort-design
            class="fts-col"
          />
          <!-- celebrate -->
          <fts-form-element
            :item="{
              fieldName: 'celebrate',
              input: 'toggleSwitch',
            }"
            :form-data="formData"
            comfort-design
            class="fts-col"
            v-if="isBeta"
          >
          
            <template #formLabel="{ label }">
              {{label}} <fts-release-badge beta class="fts-align-top" />
            </template>
          </fts-form-element>
          <!-- disableUser -->
          <fts-form-element
            :item="{
              fieldName: 'disableUser',
              input: 'toggleSwitch',
            }"
            :form-data="formData"
            comfort-design
            class="fts-col"
            v-if="isBeta"
          >
          
            <template #formLabel="{ label }">
              {{label}} <fts-release-badge beta class="fts-align-top" />
            </template>
          </fts-form-element>
        </div>
      </fts-collapsible-element>
    </template>
    <!-- footer -->
    <template #footerLeft>
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
    </template>
    <template #footerRight>
      <fts-btn text @click="beforeClose">Cancel</fts-btn>

      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" />Ok
      </fts-btn>

      <fts-btn outline color="primary" @click="doneContinue" v-if="continuable">
        <font-awesome-icon :icon="['fas', 'chevron-right']" />Continue
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import Swatches from "vue-swatches";
import changesets from "diff-json";

import canvasDesign from "../commons/canvasDesign";
import commons from '../commons/commons';
import validator from "../commons/designValidator";
import { darkPalette } from "../commons/colors";
import { authorizationStore } from '../store/authorizationStore';

import FTSDesignModal from "./FTSDesignModal";
import FTSStateDesignInline from "./FTSStateDesignInline";
import FTSActivityDesignInline from "./FTSActivityDesignInline";
import FTSBtn from "../elements/FTSBtn";
import FTSFormElement from "../elements/FTSFormElement";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSState from "../elements/FTSState";
import FTSTippy from "../elements/FTSTippy";
import FTSReleaseBadge from '../elements/FTSReleaseBadge';
import FTSAiAnnotation from "./FTSAiAnnotation";

export default {
  props: {
    builder: Boolean
  },
  components: {
    Swatches,
    "fts-design-modal": FTSDesignModal,
    "fts-state-design-inline": FTSStateDesignInline,
    "fts-activity-design-inline": FTSActivityDesignInline,
    "fts-btn": FTSBtn,
    "fts-form-element": FTSFormElement,
    "fts-contenteditable": FTSContenteditable,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-state": FTSState,
    "fts-tippy": FTSTippy,
    "fts-release-badge": FTSReleaseBadge,
    "fts-ai-annotation": FTSAiAnnotation
  },
  data: () => ({
    formData: null,
    originalData: null,
    resolve: null,
    continueCallBack: null,
    navigateCallback: null,
    params: null,
    advanced: false,
    autofillLoading: false,
    designNameHasFocus: false
  }),
  validations() {
    let formData = {};
    formData = validator.stateDesign(this.states);
    return { formData };
  },
  methods: {
    async show(item, saveCallBack, continueCallBack, navigateCallback, params) {
      this.formData = _.cloneDeep(item);
      this.continueCallBack = continueCallBack;
      this.navigateCallback = navigateCallback;
      this.params = params;

      if (this.isExist) {
        const { t, tid } = this.$route.query;
        if (!t && !tid) {
          commons.addDesignQueryParams(this.$router, this.$route, [
            { name: "t", value: "state" },
            { name: "tid", value: this.formData.id }
          ]);
        }
      }

      /* create first state, auto-set initial */
      if (!this.states || (this.states && this.states.length === 0))
        this.$set(this.formData, "initial", true);

      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              event.stopImmediatePropagation();
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    }
                  }
                ]
              });
            } else {
              this.reset();
            }
          }
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          this.$refs.designerName.focus();
          this.designNameHasFocus = true;
          if (this.formData.name)
            this.$refs.designerName.style.width = this.calculateInputWidth();
        });
      });
    },
    done() {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        this.resolve && this.resolve(this.formData);
        this.reset();
      }
    },
    doneContinue() {
      if (!this.continuable) return;

      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        const formData = _.cloneDeep(this.formData);
        const continueCallBack = Object.assign(this.continueCallBack);
        /* reset */
        this.reset();
        continueCallBack && continueCallBack(formData);
        this.$toasted.info(`State: ${formData.name} is added.`);
      }
    },
    navigateContinue(type, item, params) {
      function navigate(vm) {
        const formData = _.cloneDeep(vm.formData);
        const navigateCallback = Object.assign(vm.navigateCallback);
        /* reset */
        vm.reset();
        /* navigate */
        navigateCallback && navigateCallback(formData, type, item, params);
      }

      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        if (this.isExist) {
          const diff = changesets.diff(this.formData, this.originalData);
          if (diff && diff.length) {
            this.$modal.show("dialog", {
              title: "Confirmation",
              text: "Keep changes and continue?",
              buttons: [
                { title: "Cancel" },
                {
                  title: "Continue",
                  default: true,
                  handler: () => {
                    this.$modal.hide("dialog");
                    navigate(this);
                  }
                }
              ]
            });
          } else {
            navigate(this);
          }
        } else {
          navigate(this);
        }
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    async remove() {
      const res = await this.$confirmation.show({
        type: 'delete',
        title: 'State',
        text: this.formData.name,
        backgroundColor: this.formData.color
      });

      if (res === true) {
        this.resolve && this.resolve(true);
        this.reset();
      }
    },
    reset() {
      const { t, tid } = this.$route.query;
      if (t === "state" && tid === this.formData.id) {
        commons.removeDesignQueryParams(this.$router, this.$route, ["t", "tid"]);
      }

      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    debounceInput: _.debounce(async vm => {
      if (vm.formData && vm.formData.name)
        try {
          const res = await vm.$store.dispatch("ai", {
            type: "state",
            text: vm.formData.name
          });
          vm.$set(vm.formData, "color", res.data);
        } catch (err) {
          console.error(err);
        } finally {
          vm.autofillLoading = false;
        }
    }, 800),
    dispatchAutofill() {
      if (!this.isExist) {
        if (!this.autofillLoading) this.autofillLoading = true;
        this.debounceInput(this);
      }
    },
    error(key) {
      return validator.stateDesignError(this.$v.formData, key);
    },
    calculateInputWidth() {
      try {
        const fontSize = window
          .getComputedStyle(this.$refs.designerName, null)
          .getPropertyValue("font-size");
        const fontWeight = window
          .getComputedStyle(this.$refs.designerName, null)
          .getPropertyValue("font-weight");
        const fontFamily = window
          .getComputedStyle(this.$refs.designerName, null)
          .getPropertyValue("font-family");
        const pl = parseFloat(
          window
            .getComputedStyle(this.$refs.designerName, null)
            .getPropertyValue("padding-left") || 0
        );
        const pr = parseFloat(
          window
            .getComputedStyle(this.$refs.designerName, null)
            .getPropertyValue("padding-right") || 0
        );
        let canvas = document.createElement("canvas");
        let ctx = canvas.getContext("2d");
        ctx.font = `${fontWeight} ${fontSize} ${fontFamily}`;
        const ctxWidth = ctx.measureText(this.formData.name).width;
        return `${ctxWidth + pl + pr}px`;
      } catch (err) {
        console.error(err);
      }
    },
    navigateStateDesign(id) {
      const result = this.states.find(x => x.id === id);
      if (result) this.navigateContinue(canvasDesign.state, result);
      else this.$toasted.error("Something went wrong. State not found.");
    },
    navigateActivityDesign(id) {
      const result = this.activities.find(x => x.id === id);
      if (result) this.navigateContinue(canvasDesign.activity, result);
      else this.$toasted.error("Something went wrong. Activity not found.");
    },
    navigateNewActivityDesign(id) {
      if (id) {
        const params = { fromStateId: id, disableContinue: true };
        this.navigateContinue(canvasDesign.activity, null, params);
      }
    },
    navigateStandardDesign(id) {
      const result = this.standardActivities.find(x => x.id === id);
      if (result) this.navigateContinue(canvasDesign.standard, result);
      else this.$toasted.error("Something went wrong. Activity not found.");
    },
    openWorkflow() {
      this.$openStudioWorkflow && this.$openStudioWorkflow();
    },
  },
  computed: {
    isBeta() {
      return authorizationStore.getters.isBeta;
    },
    states() {
      return this.$store.getters.canvasStates;
    },
    standardActivities() {
      return this.$store.getters.canvasStandardActivities;
    },
    activities() {
      return this.$store.getters.canvasActivities;
    },
    colors() {
      return darkPalette;
    },
    isExist() {
      return this.states.some(x => x.id === this.formData.id);
    },
    isAdvancedSettings() {
      return !Boolean(this.params && this.params.disableAdvanced);
    },
    getStyle() {
      if (this.formData && this.formData.name && this.formData.color)
        return {
          background: this.formData.color,
          color: "#fff"
        };
    },
    getInputWidth() {
      if (this.formData && this.formData.name && this.$refs.designerName)
        return {
          width: this.calculateInputWidth()
        };
    },
    headerHintHelper() {
      return (
        this.designNameHasFocus &&
        !this.autofillLoading &&
        !this.isExist &&
        this.continuable &&
        this.formData &&
        this.formData.name &&
        this.$v.formData &&
        !this.$v.formData.$error
      );
    },
    continuable() {
      return !this.isExist && Boolean(this.continueCallBack);
    },
    initStandardId() {
      const standardCreate = this.standardActivities.find(
        x => x.id === "create"
      );
      return standardCreate && standardCreate.design
        ? "create"
        : "createEditView";
    },
    stateFlows() {
      return [
        {
          id: "toState",
          name: "Incoming - possible activities that leads to this state",
          states: this.$store.getters.canvasFlows.filter(
            x =>
              x.fromStateId != this.formData.id &&
              ((!x.conditional && x.toStateId == this.formData.id) ||
                (x.conditional &&
                  x.conditionalStates.some(
                    y => y.toStateId == this.formData.id
                  )))
          )
        },
        {
          id: "loopState",
          name: "Loop - possible activities can be performed under this state",
          states: this.$store.getters.canvasFlows.filter(
            x =>
              x.fromStateId == this.formData.id &&
              ((!x.conditional && x.toStateId == this.formData.id) ||
                (x.conditional &&
                  x.conditionalStates.some(
                    y => y.toStateId == this.formData.id
                  )))
          )
        },
        {
          id: "fromState",
          name:
            "Outgoing - possible activities that can be performed under this state",
          states: this.$store.getters.canvasFlows.filter(
            x =>
              x.fromStateId == this.formData.id &&
              !(!x.conditional && x.toStateId == this.formData.id) &&
              !(
                x.conditional &&
                x.conditionalStates.some(y => y.toStateId == this.formData.id)
              )
          )
        }
      ];
    }
  }
};
</script>

<style lang="scss">
.fts-state-design-modal {
  .state-design-modal-header {
    margin-bottom: 0.375rem;
  }
  .state-design-input-wrapper {
    min-width: 0;
    flex: 1;
    > input {
      max-width: 100%;
    }
  }
}
</style>